// src/pages/PlotDetails.js
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  DollarSign,
  User,
  CreditCard,
  Briefcase,
  Download,
  Trash2,
  FilePlus,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  TrendingUp,
  TrendingDown,
  Building,
  Calendar,
  FileText,
  Upload,
  X,
} from "lucide-react";

export default function PlotDetails() {
  const { plotId } = useParams();
  const navigate = useNavigate();

  // refs
  const fileInputRef = useRef(null);

  // state
  const [receipts, setReceipts] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [purchasePrice, setPurchasePrice] = useState("N/A");
  const [bankAccount, setBankAccount] = useState("N/A");
  const [customerName, setCustomerName] = useState("N/A");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, name: "" });

  useEffect(() => {
    const fetchPlotDetails = async () => {
      try {
        // 1) fetch receipts & plot info
        const { data } = await axios.get(
          `https://workspace.optiven.co.ke/api/mib/plots/${encodeURIComponent(
            plotId
          )}`
        );
        const receiptsData = data.receipts
          ? Array.isArray(data.receipts)
            ? data.receipts
            : [data.receipts]
          : [];
        setReceipts(receiptsData);
        if (receiptsData.length > 0) {
          const first = receiptsData[0];
          setPurchasePrice(first.purchase_price ?? "N/A");
          setBankAccount(first.Bank_Account ?? "N/A");
          setCustomerName(first.Customer_Name ?? "N/A");
        }

        // 2) fetch existing agreements
        const agRes = await axios.get(
          `https://workspace.optiven.co.ke/api/sla/plots/${encodeURIComponent(
            plotId
          )}/agreements`
        );
        setAgreements(agRes.data);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 404) {
          toast.info("No payment records or agreements found for this plot.");
        } else {
          setError("Failed to load plot details.");
          toast.error("Failed to load plot details.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlotDetails();
  }, [plotId]);

  const formatDate = (d) => {
    if (!d) return "N/A";
    const t = Date.parse(d);
    if (isNaN(t)) return d;
    return new Date(t).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // totals & rules
  const totalPaid = receipts.reduce(
    (sum, r) => sum + (parseFloat(r.Amount_LCY) || 0),
    0
  );
  const purchaseNum = purchasePrice !== "N/A" ? parseFloat(purchasePrice) : 0;
  const isUnder = totalPaid < purchaseNum;
  const hasAgreement = agreements.length > 0;
  const canDischarge = !isUnder && hasAgreement;

  // upload new agreement
  const handleUpload = async () => {
    if (!file) return toast.error("Please select a file first.");
    const form = new FormData();
    form.append("agreement", file);
    try {
      await axios.post(
        `https://workspace.optiven.co.ke/api/sla/plots/${encodeURIComponent(
          plotId
        )}/agreements`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Agreement uploaded.");
      // refresh list
      const { data } = await axios.get(
        `https://workspace.optiven.co.ke/api/sla/plots/${encodeURIComponent(
          plotId
        )}/agreements`
      );
      setAgreements(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      toast.error("Upload failed.");
    }
  };

  // delete an agreement
  const confirmDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `https://workspace.optiven.co.ke/api/sla/plots/${encodeURIComponent(
          plotId
        )}/agreements/${deleteTarget.id}`
      );
      setAgreements(agreements.filter((a) => a.id !== deleteTarget.id));
      toast.success("Agreement removed.");
    } catch (err) {
      console.error(err);
      toast.error("Could not delete agreement.");
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // discharge logic
  const handleDischarge = async () => {
    if (isUnder) {
      return toast.error(
        "Cannot discharge: total paid is less than purchase price."
      );
    }
    if (!hasAgreement) {
      return toast.error("Cannot discharge: no signed sales agreement.");
    }
    try {
      await axios.patch(
        `https://workspace.optiven.co.ke/api/mib/plots/${encodeURIComponent(
          plotId
        )}/discharge`,
        { discharge: "initiated" }
      );
      toast.success("Discharge process initiated.");
      navigate("/discharge-approvals");
    } catch (err) {
      console.error(err);
      toast.error("Failed to initiate discharge.");
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading plot details...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch the latest data
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Plot Details
            </h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Plot {plotId}
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Detailed information and payment records
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">
                    Ksh. {totalPaid.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Purchase Price</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {purchasePrice !== "N/A"
                      ? `Ksh. ${purchaseNum.toLocaleString()}`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Summary Cards */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Purchase Price
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {purchasePrice !== "N/A"
                      ? `Ksh. ${purchaseNum.toLocaleString()}`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Bank Account
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {bankAccount}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    Customer Name
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {customerName}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-500">
                    Total Paid
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    Ksh. {totalPaid.toLocaleString()}
                  </p>
                  {totalPaid > purchaseNum && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-2">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      Overpayment: Ksh.{" "}
                      {(totalPaid - purchaseNum).toLocaleString()}
                    </span>
                  )}
                  {totalPaid < purchaseNum && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-2">
                      <TrendingDown className="h-3 w-3 mr-1" />
                      Underpaid: Ksh.{" "}
                      {(purchaseNum - totalPaid).toLocaleString()}
                    </span>
                  )}
                  {totalPaid === purchaseNum && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Fully Paid
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Records Table */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Payment Records
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {receipts.length} payment record
                {receipts.length !== 1 ? "s" : ""} found
              </p>
            </div>
            {receipts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reference
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Narration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {receipts.map((r, i) => (
                      <tr key={r.id ?? i} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {i + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {r.Pay_mode ?? "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {r.Amount_LCY
                            ? `Ksh. ${parseFloat(
                                r.Amount_LCY
                              ).toLocaleString()}`
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(r.Payment_date || r.PAYMENT_DATE1)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {r.Receipt_No ?? "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div
                            className="max-w-xs truncate"
                            title={r.Narration ?? "N/A"}
                          >
                            {r.Narration ?? "N/A"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No payment records
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No payment records have been found for this plot.
                </p>
              </div>
            )}
          </div>

          {/* Sales Agreements Section */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Sales Agreements
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {agreements.length} agreement
                {agreements.length !== 1 ? "s" : ""} uploaded
              </p>
            </div>

            {/* Upload Section */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload New Agreement
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                <button
                  onClick={handleUpload}
                  disabled={!file}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </button>
              </div>
            </div>

            {/* Agreements List */}
            <div className="p-6">
              {agreements.length > 0 ? (
                <div className="space-y-3">
                  {agreements.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <FileText className="h-4 w-4 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {a.original_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded{" "}
                            {new Date(a.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={`https://workspace.optiven.co.ke/api/sla/plots/${plotId}/agreements/${a.id}`}
                          download
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </a>
                        <button
                          onClick={() => confirmDelete(a.id, a.original_name)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No agreements uploaded
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Upload sales agreements to proceed with discharge.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Discharge Action Section */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Discharge Action
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {canDischarge
                  ? "All requirements met. Ready to initiate discharge."
                  : "Complete all requirements before initiating discharge."}
              </p>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-center">
                <button
                  onClick={handleDischarge}
                  disabled={isUnder || !hasAgreement}
                  className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    canDischarge
                      ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {canDischarge
                    ? "Submit to Discharge"
                    : "Requirements Not Met"}
                </button>
              </div>

              {!canDischarge && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
                    <p className="text-sm text-yellow-700">
                      Please ensure all required documents are uploaded and plot
                      status is correct before initiating discharge.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setIsDeleteModalOpen(false)}
              ></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Delete Agreement
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete{" "}
                          <span className="font-medium text-gray-900">
                            {deleteTarget.name}
                          </span>
                          ? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={handleDelete}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          pauseOnHover
          draggable
        />
      </div>
    </Sidebar>
  );
}
